import { Trans, useTranslation } from "react-i18next";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import whitelabel from "../../whitelabel";
import { Location, useLocation } from "react-router";
import {
	DeviceCategory,
	GetAllDevicesParams,
	VelavuAPI,
	VelavuAsset,
	VelavuDevice,
} from "velavu-js-api";
import ConnectionStatus from "../../data/connection-status";
import IconAdd from "../../dynamicicons/icon-add";
import IconRefresh from "../../dynamicicons/icon-refresh";
import IconVelavuDevices from "../../dynamicicons/icon-velavu-devices";
import Checkbox from "../../elements/checkbox";
import DeviceFilterMenu from "../../elements/device-filter-menu";
import DeviceIcon from "../../elements/device-icon";
import Divider from "../../elements/divider";
import DynamicBattery from "../../elements/dynamic-battery";
import ListHeader from "../../elements/list-header";
import ListPagination from "../../elements/list-pagination";
import LoadingDots from "../../elements/loading-dots";
import StatusIndicator from "../../elements/status-indicator";
import ToggleableFade from "../../elements/toggleable-fade";
import VelavuButton from "../../elements/velavu-button";
import { WeightedTable } from "../../elements/weighted-table";
import { useVelavuModal } from "../../elements/velavu-modal";
import { VelavuTitledModal } from "../../elements/velavu-titled-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuAlertModalTextConfirm from "../../elements/velavu-alert-modal/velavu-alert-modal-text-confirm";
import { makeStyleable } from "../../helper/icon-helper";
import {
	mapDeviceCategory,
	mapDeviceModel,
} from "../../helper/language-helper";
import { deepCompare } from "../../helper/object-helper";
import { getDeviceStatus } from "../../helper/status-helper";
import { classArr } from "../../helper/style-helper";
import { NoDevicesIcon } from "../../img/icons/icons";
import ScreenContainer from "../../screens/screen-container";
import { useListDevicesState } from "../../state/list-devices-state";
import DetailModal from "../asset-tag-detail/detail-modal";
import * as styles from "./devices.module.scss";
import * as tableStyles from "../../styles/table.module.scss";
import ModalRegisterTag from "./register/modal-register-tag";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import IconCheckCircle from "../../dynamicicons/icon-check-circle";
import { ScreenContentContainer } from "../../screens/screen-content-container";
import { useHasPermission } from "../sign-in-context/sign-in-context";
import { getPermissionDeniedMessage } from "../../helper/permission-helper";

interface TagsHistoryState {
	deviceID?: string;
}

export default function Devices() {
	const { t } = useTranslation();

	useEffect(() => {
		document.title = `${whitelabel.name} | ${t("devices.page_title")}`;
	}, [t]);

	return (
		<ScreenContainer>
			<TagsList />
		</ScreenContainer>
	);
}

function NoDevices() {
	const { t } = useTranslation();

	return (
		<div className={styles.emptyContent}>
			<NoDevicesIcon />
			<span className={styles.heading}>
				{t("devices.no_devices_heading")}
			</span>
			<span className={styles.info}>{t("devices.no_devices_info")}</span>
		</div>
	);
}

interface DeviceActionInProgessModalProps {
	title: string;
	action: string;
	progressCurrent: number;
	progressTotal: number;
}

function DeviceActionInProgessModal(props: DeviceActionInProgessModalProps) {
	const { title, action, progressCurrent, progressTotal } = props;
	const { t } = useTranslation();

	const actionText = useMemo(() => {
		switch (action) {
			case "delete":
				return t("devices.progress_deleted");
			default:
				return t("devices.progress_rebooted");
		}
	}, [action, t]);

	return (
		<ToggleableFade
			visible={progressCurrent !== progressTotal}
			shift
			className={styles.progressScrim}
		>
			<div className={styles.progressContainer}>
				<div className={styles.progressHeader}>{title}</div>
				<Divider />
				<div className={styles.progressBody}>
					{`${progressCurrent}/${progressTotal} ${actionText}`}
				</div>
			</div>
		</ToggleableFade>
	);
}

function DeviceId(props: { device: VelavuDevice }) {
	return (
		<div className={styles.deviceLabel}>
			<StatusIndicator status={getDeviceStatus(props.device)} />
			<div className={styles.deviceId}>{props.device.id}</div>
		</div>
	);
}

function DevicesTableRow(props: {
	device: VelavuDevice;
	onDetails: VoidFunction;
	onPair: VoidFunction;
	onReboot: VoidFunction;
	onSelect: (selected?: boolean) => void;
	selected: boolean;
	canPair?: boolean;
}) {
	const { t } = useTranslation();

	const handlePair = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			props.onPair();
		},
		[props.onPair],
	);

	return (
		<tr className={styles.tableRow}>
			{/* Checkbox */}
			<td>
				<Checkbox checked={props.selected} onClick={props.onSelect} />
			</td>

			{/* Device ID */}
			<td onClick={props.onDetails}>
				<DeviceId device={props.device} />
			</td>

			{/* Device model */}
			<td onClick={props.onDetails}>
				<DeviceIcon
					className={styles.tableCellIcon}
					model={props.device.model}
				/>
				<span className={styles.tableCell} style={{ marginLeft: 8 }}>
					{mapDeviceModel(props.device.model, t)}
				</span>
			</td>

			{/* Device type/category */}
			<td onClick={props.onDetails}>
				<span className={styles.tableCell}>
					{mapDeviceCategory(props.device.category, t)}
				</span>
			</td>

			{/* Asset */}
			<td onClick={props.onDetails}>
				{props.device.asset !== undefined ? (
					<AssetName asset={props.device.asset} />
				) : props.device.category === DeviceCategory.Tag ? (
					<VelavuButton
						label={t("devices.pair_asset")}
						icon={(size, color, className) => (
							<IconAdd
								size={size}
								color={color}
								className={className}
							/>
						)}
						size="small"
						disabled={!props.canPair}
						tooltip={
							!props.canPair
								? getPermissionDeniedMessage("assets:write", t)
								: undefined
						}
						onClick={handlePair}
					/>
				) : (
					<span>&#8212;</span>
				)}
			</td>

			{/* Battery */}
			<td onClick={props.onDetails}>
				<DynamicBattery
					className={styles.tableCellIcon}
					level={
						props.device.state?.power?.battery_level !== undefined
							? props.device.state?.power.battery_level / 100
							: -1
					}
					charging={
						props.device.state !== undefined &&
						props.device.state.power !== undefined &&
						"usb_power" in props.device.state.power &&
						props.device.state.power.usb_power
					}
				/>
				<span className={styles.tableCell} style={{ marginLeft: 4 }}>
					{props.device.state?.power?.battery_level !== undefined
						? `${props.device.state.power.battery_level}%`
						: t("common.na")}
				</span>
			</td>

			{/* Configured */}
			<td onClick={props.onDetails}>
				{props.device.config_confirmed ? (
					<IconCheckCircle
						className={styles.tableCellIcon}
						size={16}
						color={whitelabel.palette.active900}
					/>
				) : (
					<IconRefresh className={styles.tableCellIcon} size={16} />
				)}
			</td>

			{/* Actions column */}
			<td onClick={props.onDetails} />
		</tr>
	);
}

function AssetName(props: { asset: VelavuAsset }) {
	return (
		<div className={classArr(styles.assetName, styles.tableCell)}>
			{props.asset.name}
		</div>
	);
}

function Loading() {
	return (
		<div className={styles.loading}>
			<LoadingDots size={40} color={whitelabel.palette.secondary500} />
		</div>
	);
}

function TagsList() {
	const { t } = useTranslation();
	const location = useLocation() as Location<TagsHistoryState | null>;

	const canWriteDevices = useHasPermission("devices:write");
	const canManageDevices = useHasPermission("devices:manage");
	const canDeleteDevices = useHasPermission("devices:delete");
	const canWriteAssets = useHasPermission("assets:write");

	const [searchText, setSearchText] = useState("");

	const scrollableBodyRef = useRef<HTMLDivElement | null>(null);
	const [scrollToTop, setScrollToTop] = useState(false);
	useEffect(() => {
		if (scrollToTop) {
			scrollableBodyRef.current?.scrollTo(0, 0);
			setScrollToTop(false);
		}
	}, [scrollToTop, setScrollToTop]);

	const [actionProgress, setActionProgress] = useState(0);
	const [actionInProgress, setActionInProgress] = useState<
		"unregister" | "reboot" | undefined
	>(undefined);

	const [totalPages, setTotalPages] = useState<number>(1);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [devicesState, devicesActions] = useListDevicesState();
	const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

	const [filterStatus, setFilterStatus] = useState<ConnectionStatus | null>(
		null,
	);
	const [filterUnpaired, setFilterUnpaired] = useState(false);
	const [filterUnconfigured, setFilterUnconfigured] = useState(false);
	const [filterCategory, setFilterCategory] = useState<DeviceCategory | null>(
		null,
	);
	const [filterModel, setFilterModel] = useState<string | null>(null);
	const [appliedDeviceFilters, setAppliedDeviceFilters] =
		useState<GetAllDevicesParams>({
			online: undefined,
			hardware: undefined,
			category: undefined,
			paired: undefined,
			onlyRegistered: undefined,
			configConfirmed: undefined,
		});
	const deviceFilters = useMemo((): GetAllDevicesParams => {
		let online: boolean | undefined;
		switch (filterStatus) {
			case "active":
				online = true;
				break;
			case "inactive":
				online = false;
				break;
			default:
				online = undefined;
				break;
		}

		return {
			online: online,
			model: filterModel ?? undefined,
			category: filterCategory ?? undefined,
			paired: filterUnpaired ? false : undefined,
			onlyRegistered: filterStatus === "registered" ? true : undefined,
			configConfirmed: filterUnconfigured ? false : undefined,
		};
	}, [
		filterCategory,
		filterModel,
		filterStatus,
		filterUnconfigured,
		filterUnpaired,
	]);

	const isDirty = useMemo(() => {
		return !deepCompare(appliedDeviceFilters, deviceFilters);
	}, [appliedDeviceFilters, deviceFilters]);

	const devicesActionsLoadDevices = devicesActions.loadDevices;
	const refetchDevices = useCallback(
		(params: GetAllDevicesParams) => {
			setTotalPages(1);
			setCurrentPage(1);
			return devicesActionsLoadDevices(params);
		},
		[setTotalPages, setCurrentPage, devicesActionsLoadDevices],
	);

	const activeGetDevicesParams = useMemo<GetAllDevicesParams>(() => {
		//Load assets with the combined filters and search query
		let cleanSearchText: string | undefined = searchText.trim();
		if (cleanSearchText.length === 0) {
			cleanSearchText = undefined;
		}
		return {
			...appliedDeviceFilters,
			id: cleanSearchText,
		};
	}, [searchText, appliedDeviceFilters]);

	const refetchEffectFirstMount = useRef(true);
	const refetchEffectLastAppliedDeviceFilters = useRef(appliedDeviceFilters);
	useEffect(() => {
		//Don't run on first mount to avoid double loading
		if (refetchEffectFirstMount.current) {
			refetchEffectFirstMount.current = false;
			return;
		}

		//If the device filters have changed, apply the update immediately
		if (
			!deepCompare(
				refetchEffectLastAppliedDeviceFilters.current,
				appliedDeviceFilters,
			)
		) {
			refetchEffectLastAppliedDeviceFilters.current =
				appliedDeviceFilters;
			refetchDevices(activeGetDevicesParams);
		} else {
			//Debounce the refetch to avoid spamming the server
			//as the user is typing
			const timeoutID = window.setTimeout(() => {
				refetchDevices(activeGetDevicesParams);
			}, 300);
			return () => {
				window.clearTimeout(timeoutID);
			};
		}
	}, [refetchDevices, appliedDeviceFilters, activeGetDevicesParams]);

	//Register the refreshable provider
	useProvideContentRefreshable(
		useCallback(
			() => refetchDevices(activeGetDevicesParams),
			[refetchDevices, activeGetDevicesParams],
		),
	);

	const applyDeviceFilters = useCallback(() => {
		setAppliedDeviceFilters(deviceFilters);
	}, [setAppliedDeviceFilters, deviceFilters]);

	const displayDevices = useMemo(() => {
		// Render assets based on page number (limit of 250 per page)
		const startIndex = (currentPage - 1) * 250;
		const endIndex = currentPage * 250;

		return devicesState.devices.slice(startIndex, endIndex);
	}, [devicesState.devices, currentPage]);

	const loadMoreDevices = useCallback(() => {
		if (devicesState.hasMore) {
			devicesActions.loadMore();
			setTotalPages((totalPages) => totalPages + 1);
		}

		setCurrentPage((currentPage) => currentPage + 1);
	}, [devicesState.hasMore, devicesActions]);

	const { pushModal } = useVelavuModal();
	const inspectDevice = useCallback(
		(
			device: VelavuDevice,
			defaultOpenPair?: undefined | "asset" | "tag",
		) => {
			pushModal(
				(close) => (
					<VelavuTitledModal
						title={t("devices.device_details")}
						onClose={close}
					>
						<DetailModal
							asset={device.asset}
							device={device}
							deviceOnly={
								device.category === DeviceCategory.Anchor
							}
							defaultOpenPair={defaultOpenPair}
							defaultTab="device"
							onClose={close}
						/>
					</VelavuTitledModal>
				),
				{ pinToTop: true },
			);
		},
		[pushModal, devicesActions, t],
	);

	const registerTag = useCallback(() => {
		pushModal((close) => (
			<ModalRegisterTag
				onClose={close}
				onRegister={(newTag) => devicesActions.registerDevice(newTag)}
			/>
		));
	}, [devicesActions, pushModal]);

	const unregisterSelectedTags = useCallback(() => {
		const confirmUnregisterText = t("devices.confirm_unregister");
		pushModal<boolean>((resolve) => (
			<VelavuAlertModalTextConfirm
				title={t("devices.unregister_title", {
					count: selectedDevices.length,
				})}
				confirmText={confirmUnregisterText}
				confirmLabel={t("common.delete")}
				isDanger
				onResolve={resolve}
			>
				<Trans
					i18nKey="devices.unregister_confirm_instruction"
					values={{ confirmText: confirmUnregisterText }}
					components={{ i: <i /> }}
					parent="span"
				/>
			</VelavuAlertModalTextConfirm>
		)).then(async (result) => {
			if (result) {
				setActionInProgress("unregister");

				for (let i = 0; i < selectedDevices.length; i++) {
					try {
						devicesActions.unregisterDevice(selectedDevices[i]);
						setActionProgress(i + 1);
					} catch (error) {
						console.error(error);
					}
				}

				setSelectedDevices([]);
				setActionInProgress(undefined);
				setActionProgress(0);
			}
		});

		return () => {
			setActionInProgress(undefined);
		};
	}, [pushModal, selectedDevices, devicesActions, t]);

	const rebootTag = useCallback((tag: VelavuDevice) => {
		VelavuAPI.devices
			.rebootSpecificDevice(tag.id)
			.then(() => {})
			.catch(console.log);
	}, []);

	const rebootSelectedTags = useCallback(() => {
		pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("devices.reboot_modal_title", {
						count: selectedDevices.length,
					})}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					<span>{t("devices.reboot_modal_note")}</span>
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.reboot")}
						variant="normal"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		)).then(async (result) => {
			if (result) {
				setActionInProgress("reboot");

				for (let i = 0; i < selectedDevices.length; i++) {
					try {
						await VelavuAPI.devices.rebootSpecificDevice(
							selectedDevices[i],
						);
						setActionProgress(i + 1);
					} catch (error) {
						console.error(error);
					}
				}
				setSelectedDevices([]);
				setActionInProgress(undefined);
				setActionProgress(0);
			}
		});

		return () => {
			setActionInProgress(undefined);
		};
	}, [pushModal, selectedDevices, t]);

	const selectDevice = useCallback(
		(selected: boolean, deviceID: string) => {
			setSelectedDevices((selectedDevices) => {
				if (selected) {
					return [...selectedDevices, deviceID];
				} else {
					return selectedDevices.filter((id) => id !== deviceID);
				}
			});
		},
		[setSelectedDevices],
	);

	const selectAllDevices = useCallback(() => {
		setSelectedDevices((selectedDevices) => {
			if (selectedDevices.length === displayDevices.length) {
				return [];
			} else {
				return displayDevices.map((device) => device.id);
			}
		});
	}, [displayDevices]);

	//Reset the model filter when the category changes
	useEffect(() => {
		setFilterModel(null);
	}, [filterCategory, setFilterModel]);

	//Handle navigation
	useEffect(() => {
		const deviceID = location.state?.deviceID;
		if (deviceID === undefined || devicesState.devices === undefined)
			return;

		//Get the specified tag
		const tag = devicesState.devices.find((tag) => tag.id === deviceID);
		if (tag === undefined) return;

		//Inspect the tag
		inspectDevice(tag);

		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state?.deviceID, devicesState.devices]);

	return (
		<>
			{actionInProgress && (
				<DeviceActionInProgessModal
					title={
						actionInProgress === "unregister"
							? t("devices.unregistering_title")
							: t("devices.rebooting_title")
					}
					action={actionInProgress}
					progressCurrent={actionProgress}
					progressTotal={selectedDevices.length}
				/>
			)}
			<ListHeader
				icon={makeStyleable(IconVelavuDevices)}
				title={t("sidebar.nav.devices")}
				search={{
					searchPlaceholder: t("common.search"),
					searchText: searchText,
					setSearchText: setSearchText,
				}}
				add={
					canWriteDevices
						? {
								label: t("devices.register_device"),
								onClick: registerTag,
							}
						: undefined
				}
			/>

			<ScreenContentContainer className={styles.container}>
				<DeviceFilterMenu
					filterCategory={filterCategory}
					setFilterCategory={setFilterCategory}
					filterModel={filterModel}
					setFilterModel={setFilterModel}
					filterStatus={filterStatus}
					setFilterStatus={setFilterStatus}
					filterUnpaired={filterUnpaired}
					setFilterUnpaired={setFilterUnpaired}
					filterUnconfigured={filterUnconfigured}
					setFilterUnconfigured={setFilterUnconfigured}
					selectedDevices={selectedDevices}
					onReboot={rebootSelectedTags}
					onUnregister={unregisterSelectedTags}
					onApply={applyDeviceFilters}
					isDirty={isDirty}
					canReboot={canManageDevices}
					canUnregister={canDeleteDevices}
				/>

				{devicesState.isLoading ? (
					<Loading />
				) : displayDevices.length === 0 ? (
					<NoDevices />
				) : (
					<div
						ref={scrollableBodyRef}
						className={styles.tableWrapper}
					>
						<WeightedTable
							className={styles.table}
							layout={[
								{ width: 48 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1, alignment: "center" },
								{ width: 60 },
							]}
						>
							<thead
								className={classArr(
									tableStyles.scrollableTableHeader,
								)}
							>
								<tr>
									<th>
										<Checkbox
											checked={selectedDevices.length > 0}
											onClick={selectAllDevices}
											master
										/>
									</th>
									<th>{t("devices.table_device_id")}</th>
									<th>{t("devices.table_device")}</th>
									<th>{t("devices.table_type")}</th>
									<th>{t("devices.table_asset")}</th>
									<th>{t("devices.table_battery")}</th>
									<th>{t("devices.table_configured")}</th>
									<th
										aria-label={t("devices.table_actions")}
									></th>
								</tr>
							</thead>

							<tbody>
								{displayDevices.map((device) => (
									<DevicesTableRow
										key={device.id}
										device={device}
										selected={selectedDevices.includes(
											device.id,
										)}
										onSelect={(selected) =>
											selectDevice(selected!, device.id)
										}
										onDetails={() => inspectDevice(device)}
										onPair={() =>
											inspectDevice(device, "asset")
										}
										onReboot={() => rebootTag(device)}
										canPair={canWriteAssets}
									/>
								))}
							</tbody>
						</WeightedTable>
					</div>
				)}
			</ScreenContentContainer>

			<ListPagination
				totalPages={totalPages}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				setScrollToTop={setScrollToTop}
				hasMore={devicesState.hasMore}
				loadMore={loadMoreDevices}
				isLoading={devicesState.isLoading}
			/>
		</>
	);
}
